/**
 * Client-side build error logger.
 * POSTs to /log-build-error edge function for telemetry.
 *
 * Fire-and-forget pattern: returns void synchronously, async work happens internally.
 * Never throws, never blocks callers.
 */

import { supabase } from "@/integrations/supabase/client";

export type ErrorClass =
  | "missing_export"
  | "missing_file"
  | "sandbox_compile"
  | "auto_fix_timeout"
  | "edge_function_5xx"
  | "ai_couldnt_find_files"
  | "scaffold_overwrite"
  | "json_parse_failed"
  | "anthropic_timeout"
  | "truncation"
  | "empty_error_message"
  | "other";

export interface ClientBuildErrorLog {
  projectId: string;
  errorClass: ErrorClass;
  errorMessage: string;
  errorStack?: string;
  filePath?: string;
  attemptNumber?: number;
  recovered?: boolean;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

/**
 * Log a build error to the server. Fire-and-forget.
 * Returns void synchronously — async work happens internally.
 * Never throws, never rejects.
 */
export function logBuildErrorClient(log: ClientBuildErrorLog): void {
  (async () => {
    try {
      if (!SUPABASE_URL) {
        console.warn("[logBuildErrorClient] Missing SUPABASE_URL");
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn("[logBuildErrorClient] No session, skipping telemetry");
        return;
      }

      await fetch(`${SUPABASE_URL}/functions/v1/log-build-error`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(log),
      });
    } catch (e) {
      console.warn("[logBuildErrorClient] Failed to log:", e instanceof Error ? e.message : String(e));
    }
  })();
}

/**
 * Classify an error message into an ErrorClass bucket.
 * Client-side copy of server classifyBuildError for accurate buckets.
 */
export function classifyBuildError(message: string): ErrorClass {
  const m = message.toLowerCase();

  if (m.includes("no matching export") || m.includes("missing export")) {
    return "missing_export";
  }
  if (m.includes("timeout") || m.includes("180s") || m.includes("timed out")) {
    return "anthropic_timeout";
  }
  if (m.includes("scaffold") || m.includes("overwrite")) {
    return "scaffold_overwrite";
  }
  if (m.includes("missing file") || m.includes("could not resolve") || m.includes("not found")) {
    return "missing_file";
  }
  if (m.includes("esbuild") || m.includes("sandbox") || m.includes("bundle") || m.includes("compile")) {
    return "sandbox_compile";
  }
  if (m.includes("anthropic") || m.includes("api error") || /\b5\d\d\b/.test(m)) {
    return "edge_function_5xx";
  }
  if (m.includes("couldn't find") || m.includes("could not find") || m.includes("no json")) {
    return "ai_couldnt_find_files";
  }
  if (m.includes("json") || m.includes("parse")) {
    return "json_parse_failed";
  }

  return "other";
}
